import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * LandingSplash — fullscreen intro that plays the INPropPortal logo-reveal
 * video (muted) on the very first landing view of a browser session.
 *
 * • sessionStorage flag `inpp_splash_seen` guarantees it plays once per visit,
 *   NOT on every page refresh inside the same tab.
 * • Dismisses on the earlier of: video `ended` event, a hard 10s ceiling that
 *   only starts once `canplaythrough` has fired (so poor networks aren't
 *   punished for slow first-byte), or a video-load error (fail-open).
 * • Fades out with framer-motion so the transition into the landing page
 *   feels intentional, not abrupt.
 */
const SESSION_KEY = "inpp_splash_seen";
const VIDEO_SRC =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_pune-property-ai/artifacts/lpq3o2fl_INPropPortal.com%20logo%20reveal%20%282%29.mp4";
const HARD_CAP_MS = 10000;

export default function LandingSplash() {
  const [visible, setVisible] = useState(() => {
    try {
      return typeof window !== "undefined" && !sessionStorage.getItem(SESSION_KEY);
    } catch {
      return true;
    }
  });
  const videoRef = useRef(null);

  useEffect(() => {
    if (!visible) return;
    const dismiss = () => {
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* storage disabled */ }
      setVisible(false);
    };
    let timeoutId = null;
    const startCountdown = () => {
      if (timeoutId != null) return; // idempotent — fire once
      timeoutId = setTimeout(dismiss, HARD_CAP_MS);
    };
    const el = videoRef.current;
    const onEnded = () => dismiss();
    const onError = () => dismiss();
    const onCanPlayThrough = () => startCountdown();
    if (el) {
      el.addEventListener("ended", onEnded);
      el.addEventListener("error", onError);
      el.addEventListener("canplaythrough", onCanPlayThrough);
      // If the browser already has enough buffered by mount time, kick off now.
      if (el.readyState >= 4) startCountdown();
      // Force muted + autoplay for iOS Safari.
      el.muted = true;
      el.play().catch(() => { /* autoplay-blocked → canplaythrough timer still dismisses */ });
    }
    return () => {
      if (timeoutId != null) clearTimeout(timeoutId);
      if (el) {
        el.removeEventListener("ended", onEnded);
        el.removeEventListener("error", onError);
        el.removeEventListener("canplaythrough", onCanPlayThrough);
      }
    };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="landing-splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-sg-navy"
          data-testid="landing-splash"
        >
          <video
            ref={videoRef}
            src={VIDEO_SRC}
            className="w-full h-full object-cover"
            muted
            playsInline
            autoPlay
            preload="auto"
            data-testid="landing-splash-video"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
